import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

import {
  Button, Dialog, DialogContent, DialogFooter, DialogTitle, MenuItem, SelectField,
} from '../../2-component';
import { Box, Typography } from '../../1-primative';
import { useGlobalToast } from '../../../providers/globalToastProvider';
import { ClientGroup, Relationship, User } from '../../../interfaces';
import { UPDATE_CLIENTGROUP_RELATIONSHIPS } from '../../4-module/widgets/householdMembers/components/tableHouseHoldMembers';

const INITIAL_DATA = {
  id: '',
  primaryUser: {
    id: '',
    firstName: '',
    lastName: '',
  },
  relationships: [] as Relationship[],
};

const RemoveClientFromHouseholdDialog = ({
  open, setOpen, user, refetch, households,
}: { open: boolean; setOpen: (open: boolean) => void; user: User, refetch: () => void, households: ClientGroup[] }) => {
  const { t } = useTranslation('household');
  const { showToast } = useGlobalToast();
  const [data, setData] = useState(INITIAL_DATA);

  const [updateRelationships, { loading }] = useMutation(UPDATE_CLIENTGROUP_RELATIONSHIPS);

  const onClose = () => {
    setOpen(false);
    setData(INITIAL_DATA);
  };

  const removeHousehold = () => {
    const existingRelationships = data.relationships.map((relationship) => ({
      userId: relationship.user.id,
      type: relationship.type ?? '',
      accessType: relationship.accessType ?? '',
    }));

    const updatedRelationships = existingRelationships.filter((rel) => (rel.userId !== user.id));

    updateRelationships({
      variables: {
        clientGroupId: data.id,
        relationships: updatedRelationships,
      },
      onCompleted: () => {
        showToast({
          severity: 'success',
          message: t('removeFromHousehold.success'),
        });
        onClose();
        refetch();
      },
    });
  };

  const handleOnChange = (e: any) => {
    const selectedHousehold = households?.find((household) => household.id === e.target.value);
    setData({
      id: e.target.value,
      primaryUser: {
        id: selectedHousehold?.primaryUser?.id ?? '',
        firstName: selectedHousehold?.primaryUser?.firstName ?? '',
        lastName: selectedHousehold?.primaryUser?.lastName ?? '',
      },
      relationships: selectedHousehold?.relationships ?? [],
    });
  };

  return (
    <Dialog onClose={onClose} open={open} fullWidth>
      <DialogTitle onClose={onClose}>
        <Box>
          <Typography variant='headingSmall'>{t('removeFromHousehold.title')}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <SelectField fullWidth value={data.id} label={t('removeFromHousehold.householdName')} onChange={handleOnChange}
            error={data.primaryUser.id === user.id} errorText={t('removeFromHousehold.error')}
            >
            {households?.map((household) => (
              <MenuItem key={household.id} value={household.id}>{household.name}</MenuItem>
            ))}
          </SelectField>
        </Box>
      </DialogContent>
      <DialogFooter>
        <Button label={t('removeFromHousehold.confirm')} onClick={removeHousehold} disabled={loading || !data.id || data.primaryUser.id === user.id} />
      </DialogFooter>
    </Dialog>
  );
};

export default RemoveClientFromHouseholdDialog;
